/*
 * Unbound Script — persistent Add to Cart states for Horizon buttons.
 * The Horizon ProductFormComponent still owns the real cart submission,
 * cart drawer, section rendering and error handling.
 */
(() => {
  if (window.USGlobalCartButtonStates) return;
  window.USGlobalCartButtonStates = true;

  const SELECTOR = 'button[data-booksbunnii-cart-button="true"]';
  const SHOPIFY_ROOT = window.Shopify?.routes?.root || '/';
  const CART_URL = `${SHOPIFY_ROOT.replace(/\/$/, '')}/cart.js` || '/cart.js';
  const pendingTimers = new WeakMap();
  let cartRequest = null;
  let refreshTimer = null;
  let syncVersion = 0;

  const getButton = (target) => target instanceof Element ? target.closest(SELECTOR) : null;
  const getForm = (button) => button?.closest('form');
  const getVariantId = (button) => {
    const form = getForm(button);
    const input = form?.querySelector('input[name="id"]');
    return String(input?.value || button?.dataset.variantId || '');
  };
  const getLabel = (button) => button?.querySelector('.add-to-cart-button-label');

  function clearPendingTimer(button) {
    const timer = pendingTimers.get(button);
    if (timer) window.clearTimeout(timer);
    pendingTimers.delete(button);
  }

  function setAdding(button) {
    if (!button || button.dataset.booksbunniiAdded === 'true') return;
    const label = getLabel(button);
    if (label) label.textContent = button.dataset.addingLabel || 'Adding…';
    button.dataset.booksbunniiPending = 'true';
    button.classList.remove('booksbunnii-added-button');
    button.classList.add('booksbunnii-adding-button');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', button.dataset.addingLabel || 'Adding…');

    clearPendingTimer(button);
    const timer = window.setTimeout(() => {
      // Fallback for carts/apps which do not dispatch Horizon's cart:update event.
      syncButtons({ forceResetPending: true });
    }, 8000);
    pendingTimers.set(button, timer);
  }

  function setAdded(button) {
    if (!button) return;
    clearPendingTimer(button);
    const label = getLabel(button);
    if (label) label.textContent = button.dataset.addedLabel || 'Added ✓';
    delete button.dataset.booksbunniiPending;
    button.dataset.booksbunniiAdded = 'true';
    button.classList.remove('booksbunnii-adding-button');
    button.classList.add('booksbunnii-added-button');
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.removeAttribute('aria-busy');
    button.setAttribute('aria-label', button.dataset.addedLabel || 'Added ✓');

    const component = button.closest('add-to-cart-component');
    if (component) component.dataset.variantInCart = 'true';
  }

  function setNormal(button) {
    if (!button) return;
    clearPendingTimer(button);
    const label = getLabel(button);
    if (label) label.textContent = button.dataset.addLabel || 'Add to Cart';
    delete button.dataset.booksbunniiPending;
    button.dataset.booksbunniiAdded = 'false';
    button.classList.remove('booksbunnii-adding-button', 'booksbunnii-added-button');
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-label');

    const variantInput = getForm(button)?.querySelector('input[name="id"]');
    const unavailable = button.dataset.canAddToCart === 'false' || Boolean(variantInput?.disabled);
    button.disabled = unavailable;
    if (unavailable) button.setAttribute('aria-disabled', 'true');
    else button.removeAttribute('aria-disabled');

    const component = button.closest('add-to-cart-component');
    if (component) component.dataset.variantInCart = 'false';
  }

  async function fetchCart() {
    if (cartRequest) return cartRequest;
    cartRequest = fetch(CART_URL, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .finally(() => { cartRequest = null; });
    return cartRequest;
  }

  async function syncButtons({ cartData = null, forceResetPending = false } = {}) {
    const version = ++syncVersion;
    try {
      const cart = cartData && Array.isArray(cartData.items) ? cartData : await fetchCart();
      if (version !== syncVersion) return;

      const inCart = new Set(
        (cart.items || [])
          .filter((item) => Number(item.quantity) > 0)
          .map((item) => String(item.variant_id))
      );

      document.querySelectorAll(SELECTOR).forEach((button) => {
        const variantId = getVariantId(button);
        if (!variantId) return;

        if (inCart.has(variantId)) {
          setAdded(button);
        } else if (button.dataset.booksbunniiPending === 'true' && !forceResetPending) {
          // Do not let an older cart response cancel the visible Adding… state.
          return;
        } else {
          setNormal(button);
        }
      });
    } catch (error) {
      console.warn('[US cart buttons] Cart sync failed:', error);
    }
  }

  function scheduleSync(delay = 180, options = {}) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => syncButtons(options), delay);
  }

  // Show Adding immediately; do not cancel or replace Horizon's submit handler.
  document.addEventListener('click', (event) => {
    const button = getButton(event.target);
    if (!button) return;

    if (button.dataset.booksbunniiAdded === 'true' || button.dataset.booksbunniiPending === 'true') {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      scheduleSync(0);
      return;
    }

    if (!button.disabled) setAdding(button);
  }, true);

  // Keyboard/assistive form submission can submit without a pointer click.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector(SELECTOR);
    if (!button || button.disabled || button.dataset.booksbunniiAdded === 'true') return;
    setAdding(button);
  }, true);

  // CartAddEvent and CartUpdateEvent both bubble as cart:update in Horizon.
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.didError) {
      scheduleSync(80, { forceResetPending: true });
      return;
    }
    const cart = event.detail?.resource;
    if (cart && Array.isArray(cart.items)) scheduleSync(80, { cartData: cart });
    else scheduleSync(180);
  });

  document.addEventListener('cart:error', () => scheduleSync(100, { forceResetPending: true }));
  document.addEventListener('discount:update', () => scheduleSync(180));
  ['cart:updated', 'cart-updated', 'cart:change', 'shopify:section:load'].forEach((name) => {
    document.addEventListener(name, () => scheduleSync(220));
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.name === 'id') scheduleSync(180);
    if (target instanceof Element && target.matches('cart-items-component input[name="updates[]"], input[data-quantity-variant-id]')) {
      scheduleSync(300);
    }
  });

  document.addEventListener('variant:update', (event) => {
    const available = event.detail?.resource?.available;
    const scope = event.target instanceof Element
      ? (event.target.closest('product-card, .shopify-section, dialog') || event.target)
      : document;
    if (typeof available === 'boolean') {
      scope.querySelectorAll(SELECTOR).forEach((button) => {
        button.dataset.canAddToCart = available ? 'true' : 'false';
      });
    }
    scheduleSync(180);
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('cart-remove-button, [data-cart-remove], .cart-remove-button, a[href*="/cart/change"]')) {
      scheduleSync(350);
    }
  });

  window.addEventListener('pageshow', () => scheduleSync(0));
  window.addEventListener('focus', () => scheduleSync(0));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) scheduleSync(0);
  });

  // Sync newly appended product cards, e.g. infinite-scroll and recommendations.
  const observer = new MutationObserver((mutations) => {
    const hasNewButton = mutations.some((mutation) => Array.from(mutation.addedNodes || []).some((node) => {
      if (!(node instanceof Element)) return false;
      return node.matches(SELECTOR) || Boolean(node.querySelector(SELECTOR));
    }));
    if (hasNewButton) scheduleSync(300);
  });

  if (document.body) observer.observe(document.body, { childList: true, subtree: true });
  syncButtons();
})();
